import React, { Fragment, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, withRouter } from 'react-router-dom';
import { Formik, Field, Form } from 'formik';
import i18n from '../../../i18n';
import { AuthContext } from 'context/AuthProvider';
import { EmailVerificationContext } from 'context/EmailVerificationProvider';
import EmailVerificationForm from 'container/EmailVerification/EmailVerificationForm';
import ConnectMembershipResult from 'components/UI/ConnectMembershipResult/ConnectMembershipResult';
import ConfirmEmailDialog from 'components/UI/ConfirmEmailDialog/ConfirmEmailDialog';
import ConfirmModal from 'components/UI/ConfirmModal/ConfirmModal';
import ButtonWithAsync from 'components/UI/ButtonWithAsync/ButtonWithAsync';
import Alert from 'components/UI/Antd/Alert/Alert';
import Button from 'components/UI/Antd/Button/Button';
import { AntInput } from 'components/UI/Antd/AntdInputWithFormik';
import CheckCircleFilled from '@ant-design/icons/CheckCircleFilled';
import { getConnectMembershipSchema } from 'library/helpers/formUtils';
import { setStateToUrl } from 'library/helpers/url_handler';
import membership from 'services/membership.js';
import {
  FormWrapper,
  FormImage,
  Title,
  SubTitle,
  TitleInfo,
  BottomInfo,
  ConnectedCardIconWrapper
} from './MembershipsWizard.style.js';
import membershipWheel from 'assets/images/pickapier/membership-wheel.svg';
import temCard from 'assets/images/tem-card.png';

const AsyncButton = ButtonWithAsync(Button);

const initialValues = {
  card_number: ''
};

const ConnectForm = ({
  values,
  submitCount,
  submitForm,
  confirmOpened,
  setConfirmOpened
}) => {
  const { t } = useTranslation();
  return (
    <Form>
      <Field
        component={AntInput}
        className='input-field'
        name='card_number'
        size='large'
        type='text'
        placeholder={t('YOUR_CODE_PLACEHOLDER')}
        defaultValue={values.card_number}
        submitCount={submitCount}
        hasFeedback
      />
      <AsyncButton
        asyncAction={submitForm}
        className='connect-btn'
        htmlType='button'
        type='primary'
        size='large'
        label={t('CONNECT')}
      />
      <ConfirmModal
        title={t('APPROVAL_REQUIRED_TITLE')}
        confirmButtonTitle={t('APPROVE_CHANGES')}
        visible={confirmOpened}
        asyncConfirm={true}
        onConfirm={submitForm}
        onCancel={() => setConfirmOpened(false)}
      >
        <p>{t('APPROVAL_REQUIRED_DESCRIPTION_SECOND')}</p>
        <p>{t('APPROVAL_REQUIRED_DESCRIPTION_THIRD')}</p>
      </ConfirmModal>
    </Form>
  );
};

const WizardConnect = ({ onRequestFlow, onCloseDrawer, history, location }) => {
  const { t } = useTranslation();
  const connectMembershipSchema = getConnectMembershipSchema(i18n);

  const { user, refreshData } = useContext(AuthContext);
  const { onVerify } = useContext(EmailVerificationContext);

  const [error, setError] = useState(null);
  const [resultOpened, setResultOpened] = useState(false);
  const [emailResultOpened, setEmailResultOpened] = useState(false);
  const [code, setCode] = useState(null);
  const [confirmOpened, setConfirmOpened] = useState(false);

  const onCloseResult = () => {
    setResultOpened(false);
    onCloseDrawer();
    setCode(null);
  };

  const onCloseEmailResult = async () => {
    setEmailResultOpened(false);
    onCloseDrawer();
  };

  const handleConnect = async data => {
    try {
      if (user.emailVerified) {
        const res = await membership.connect(data);
        if (res && res.error_type) {
          setError(res.error_type);
          return Promise.reject();
        } else {
          setError(null);
          await refreshData({ parameters: ['vessels'] });
          setCode(data.card_number);
          setResultOpened(true);
          return Promise.resolve();
        }
      } else {
        setError(null);
        const search = setStateToUrl({ ...data, connect_membership: true });
        await onVerify({
          showModal: false,
          force: true,
          additionalSearch: search
        });
        setEmailResultOpened(true);
      }
    } catch (err) {
      console.error(err);
      return Promise.reject();
    }
  };

  const handleSubmit = async values => {
    const { card_number } = values;
    const data = {
      card_number,
      type: 'TEM'
    };

    if (confirmOpened) {
      await handleConnect(data);
      setConfirmOpened(false);
    } else {
      try {
        const checkRes = await membership.check(data);
        if (checkRes && checkRes.error_type) {
          // ToDo: decide and handle
          // UNKNOWN_MEMBERSHIP_TYPE, INSUFFICIENT_DATA
          // BOATER_MUST_HAVE_EMAIL, CUSTOMER_NOT_FOUND
          // MEMBRSHIP_ALREADY_CONNECTED errors in future
          setError(checkRes.error_type);
          return Promise.reject();
        }
        if (checkRes && checkRes.status === 'CARD_EMAIL_IS_DIFFERENT') {
          setConfirmOpened(true);
        } else {
          handleConnect(data);
        }
      } catch (err) {
        console.error(err);
        return Promise.reject();
      }
    }
  };

  return (
    <Fragment>
      <FormWrapper className='connect-form'>
        <FormImage>
          <img src={membershipWheel} alt='Membership Wheel' />
        </FormImage>
        <Title>{t('CONNECT_WIZARD_TITLE')}</Title>
        <SubTitle>{t('CONNECT_WIZARD_SUBTITLE')}</SubTitle>
        <TitleInfo>{t('CONNECT_WIZARD_INFO')}</TitleInfo>
        {error && <Alert message={t(error)} type='error' showIcon />}
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={connectMembershipSchema}
        >
          {props => (
            <ConnectForm
              {...props}
              confirmOpened={confirmOpened}
              setConfirmOpened={setConfirmOpened}
            />
          )}
        </Formik>
        <BottomInfo className='connect-bottom-info'>
          {t('DONT_HAVE_TEM_CARD_YET')}
          {'  '}
          <span style={{ cursor: 'pointer' }}>
            <Link
              className='bottom-link'
              to={'#'}
              onClick={e => {
                e.preventDefault();
                onRequestFlow();
              }}
            >
              {t('REQUEST_CARD_BUTTON')}
            </Link>
          </span>
        </BottomInfo>
      </FormWrapper>

      <ConnectMembershipResult
        visible={resultOpened}
        title={t('CONNECT_WIZARD_RESULT_TITLE')}
        onCancel={onCloseResult}
        subtitle={
          <Fragment>
            <p>
              {t('CONNECT_WIZARD_RESULT_SUBTITLE')}
              {code && <b>{code}</b>}
            </p>
          </Fragment>
        }
        actions={
          <Button
            htmlType='button'
            type='primary'
            size='large'
            onClick={onCloseResult}
          >
            {t('OK_CAPITALIZED')}
          </Button>
        }
      >
        <ConnectedCardIconWrapper>
          <CheckCircleFilled style={{ color: '#52c41a', fontSize: '33px' }} />
          <img alt={'Connect temp card'} src={temCard} />
        </ConnectedCardIconWrapper>
      </ConnectMembershipResult>

      <ConfirmEmailDialog
        visible={emailResultOpened}
        title={t('SIGN_UP_MODAL_TITLE')}
        onCancel={onCloseEmailResult}
        subtitle={
          <Fragment>
            <p>
              {t('SIGN_UP_VERIFICATION_CODE_DESC', {
                boater_email: user.email
              })}
            </p>
          </Fragment>
        }
        actions={null}
      >
        <EmailVerificationForm
          history={history}
          location={location}
          onCloseModal={onCloseEmailResult}
        />
      </ConfirmEmailDialog>
    </Fragment>
  );
};

export default withRouter(WizardConnect);
